import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaFacebook, FaSpinner, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const OnboardWithMeta = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [loginUrl, setLoginUrl] = useState("");
  const [resultModal, setResultModal] = useState({ show: false, status: "" });
  const [isLoading, setIsLoading] = useState(false);

  const [params] = useSearchParams();
  const shownRef = useRef(false);

  // Check URL parameters for success/failure status
  useEffect(() => {
    const status = params.get("status");
    if (!status || shownRef.current) return;

    shownRef.current = true; // prevent duplicate toast

    if (status === "success") {
      toast.success("Successfully connected with Meta!", {
        theme: "colored",
      });
      setResultModal({ show: true, status: "success" });
    }

    if (status === "fail" || status === "failure") {
      toast.error("Could not connect with Meta. Please try again.", {
        theme: "colored",
      });
      setResultModal({ show: true, status: "fail" });
    }
  }, [params]);

  // Fetch Meta Login URL
  const handleMetaLogin = async () => {
    if (!user?.id) {
      toast.error("User not found");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        `https://waapi.deetyasoft.com/api/facebook/login-url?userId=${user.id}&source=lms`
      );

      const url = await res.text();

      if (url) {
        setLoginUrl(url);
        setShowModal(true);
      }
    } catch (error) {
      toast.error("Failed to load Meta URL");
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Redirect to Meta Login
  const continueMeta = () => {
    if (loginUrl) {
      window.location.href = loginUrl;
    }
  };

  return (
    <div className="p-4 min-h-screen bg-[#FAF8FF] dark:bg-gray-900 shadow-md flex items-center justify-center font-sans cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg bg-gray-100 dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700"
      >
        {/* Theme Header */}
        <div className="bg-gray-800 dark:bg-[#4C2D85] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <FaFacebook className="text-[#1877F2] bg-white rounded-full text-3xl mr-3 shadow-sm" />
            <h2 className="text-xl font-bold text-white tracking-wide cfg-heading">
              Meta Integration
            </h2>
          </div>
        </div>

        {/* Dynamic Content Area */}
        <div className="p-8 bg-white dark:bg-gray-800 rounded-b-lg min-h-[350px] flex flex-col justify-center">
          <AnimatePresence mode="wait">
            
            {/* --- STATE 3: RESULT UI (Success or Fail) --- */}
            {resultModal.show ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="flex flex-col items-center text-center py-4"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                    resultModal.status === "success"
                      ? "bg-green-100 dark:bg-green-900/30"
                      : "bg-red-100 dark:bg-red-900/30"
                  }`}
                >
                  {resultModal.status === "success" ? (
                    <FaCheckCircle className="text-4xl text-green-500 dark:text-green-400" />
                  ) : (
                    <FaTimesCircle className="text-4xl text-red-500 dark:text-red-400" />
                  )}
                </motion.div>

                <h3 className="text-2xl font-bold cfg-heading text-gray-800 dark:text-white mb-3">
                  {resultModal.status === "success"
                    ? "Integration Successful!"
                    : "Integration Failed"}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-8 text-sm max-w-xs">
                  {resultModal.status === "success"
                    ? "Your Meta account has been successfully connected. You can now access all integrated features."
                    : "We couldn't connect to your Meta account. Please check your credentials and try again."}
                </p>

                <button
                  onClick={() => {
                    if (resultModal.status === "success") {
                      navigate("/dashboard");
                    } else {
                      setResultModal({ show: false, status: "" });
                    }
                  }}
                  className="w-full px-4 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium rounded-md transition-colors"
                >
                  {resultModal.status === "success"
                    ? "Continue to Dashboard"
                    : "Try Again"}
                </button>
              </motion.div>
            ) 
            
            /* --- STATE 2: CONFIRMATION UI --- */
            : showModal ? (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex flex-col items-center text-center"
              >
                <div className="w-20 h-20 bg-[#F3E8FF] dark:bg-gray-700 rounded-full flex items-center justify-center mb-6 shadow-inner">
                  <FaFacebook className="text-5xl text-[#1877F2]" />
                </div>

                <h3 className="text-2xl font-bold cfg-heading text-gray-800 dark:text-white mb-3">
                  Ready to Connect
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-8 text-sm max-w-xs">
                  You'll be redirected to Meta's official login page to securely authorize the connection.
                </p>

                <div className="w-full space-y-3">
                  <button
                    onClick={continueMeta}
                    className="w-full flex items-center justify-center px-4 py-3.5 bg-gradient-to-r from-[#8B5CF6] to-[#673AB7] text-white font-medium rounded-md hover:from-[#673AB7] hover:to-[#5E35A6] focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:ring-offset-2 shadow-lg transition-all"
                  >
                    Proceed to Meta Login
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="w-full px-4 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            ) 
            
            /* --- STATE 1: INITIAL UI --- */
            : (
              <motion.div
                key="initial"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex flex-col items-center text-center"
              >
                <div className="w-20 h-20 bg-[#F3E8FF] dark:bg-gray-700 rounded-full flex items-center justify-center mb-6 shadow-inner">
                  <FaFacebook className="text-5xl text-[#1877F2]" />
                </div>

                <h3 className="text-2xl font-bold cfg-heading text-gray-800 dark:text-white mb-3">
                  Connect Your Meta Account
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-8 text-sm max-w-sm">
                  Securely authenticate your WhatsApp Business Account to enable seamless messaging and syncing.
                </p>

                <button
                  onClick={handleMetaLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center px-4 py-3.5 bg-gradient-to-r from-[#8B5CF6] to-[#673AB7] text-white font-medium rounded-md hover:from-[#673AB7] hover:to-[#5E35A6] focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2 text-lg" />
                      Connecting...
                    </>
                  ) : (
                    "Continue with Meta"
                  )}
                </button>

                <div className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">
                  By continuing, you agree to our{" "}
                  <a
                    href="https://deetyasoft.com/whatsapp/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#673AB7] hover:underline"
                  >
                    Terms
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://deetyasoft.com/whatsapp/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#673AB7] hover:underline"
                  >
                    Privacy Policy
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardWithMeta;